/**
 * The Goals & OKRs contract — two flat routers, `goalContract` (`/goals`) and `objectiveContract`
 * (`/objectives`). `tsRestRouter` (`apps/api/src/lib/ts-rest.ts`) throws on nested sub-routers, so
 * these are two independent top-level routers registered separately in the root contract, never one
 * nesting the other — see `docs/plans/performance-management/goals-okrs-plan.md`'s "Contract & API"
 * section for the full design this file implements.
 *
 * Follows leave.contract.ts's conventions exactly (see that file's header): DTOs distinct from the
 * Prisma models, every DTO carrying what `ResourceTarget` needs to authorise it, `paginatedSchema()`
 * for every list, decimal STRINGS never numbers (invariant 2).
 *
 * APPROVAL GOES THROUGH `@hrms/workflow`, NEVER A HAND-ROLLED STATUS FLIP (CLAUDE.md invariant 6).
 * `goalSchema.status` mirrors whatever the underlying `WorkflowInstance` says when one is attached;
 * the route only ever copies that value across after calling the engine. Goal creation's workflow is
 * CONDITIONAL — present only when an active `WorkflowDefinition` for `entityType: 'goal'` exists in
 * the org (see the plan's "Workflow — on creation, conditional" section) — so `workflowInstanceId`
 * and a `pending_approval` status are both legitimately absent for an org that hasn't opted in.
 *
 * `draftCheckIn` is the one AI endpoint here (`performance.draft_goal_update`, read-only, no I/O of
 * its own) — its 503 response is "AI drafting isn't configured" (no provider resolved), never a 500.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  GOAL_CHECKIN_SOURCES,
  GOAL_CONFIDENCE,
  GOAL_LEVELS,
  GOAL_MEASURE_TYPES,
  GOAL_STATUSES,
  OBJECTIVE_STATUSES,
} from '../enums.js';
import {
  currencySchema,
  dateOnlySchema,
  errorBodySchema,
  instantSchema,
  measureValueSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  percentSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// Shared enum schemas
// ---------------------------------------------------------------------------

export const goalLevelSchema = z.enum(GOAL_LEVELS);
export const goalStatusSchema = z.enum(GOAL_STATUSES);
export const goalMeasureTypeSchema = z.enum(GOAL_MEASURE_TYPES);
export const goalConfidenceSchema = z.enum(GOAL_CONFIDENCE);
export const goalCheckInSourceSchema = z.enum(GOAL_CHECKIN_SOURCES);
export const objectiveStatusSchema = z.enum(OBJECTIVE_STATUSES);

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

/**
 * A `KeyResult` attached to an `Objective`. `currency` is required exactly when
 * `measureType === 'currency'` (CLAUDE.md invariant 2: never an amount without its currency) — the
 * route enforces this pairing; the DTO itself only carries the optional field since a `percentage`/
 * `numeric`/`boolean`/`milestone` key result has none.
 */
export const keyResultSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  objectiveId: uuidSchema,
  title: z.string(),
  measureType: goalMeasureTypeSchema,
  targetValue: measureValueSchema,
  currency: currencySchema.nullable(),
  weightPercent: percentSchema,
});
export type KeyResult = z.infer<typeof keyResultSchema>;

/**
 * The compact per-key-result row a list CARD shows (T20: "Key results + inline Check-in on
 * cards" — see the plan's gap-analysis item) — deliberately smaller than `keyResultSchema`/
 * `goalKeyResultSchema`: a card needs a title, a target/current pair, and a progress percentage,
 * never the full detail-view shape (no `orgId`/`objectiveId`/`goalId`/`weightPercent`). Used by
 * BOTH `goalSummarySchema` (the goal's own `GoalKeyResult` rows) and `objectiveSummarySchema`
 * (aggregated across every `GoalKeyResult` that rolls up to one of the Objective's `KeyResult`
 * rows — see `toObjectiveSummary` in `objective.ts` for how `currentValue`/`progressPercent` are
 * derived there, since `KeyResult` itself carries no current-value column of its own).
 */
export const goalKeyResultSummarySchema = z.object({
  id: uuidSchema,
  title: z.string(),
  measureType: goalMeasureTypeSchema,
  currency: currencySchema.nullable(),
  currentValue: measureValueSchema,
  targetValue: measureValueSchema,
  progressPercent: percentSchema,
});
export type GoalKeyResultSummary = z.infer<typeof goalKeyResultSummarySchema>;

/**
 * Cascade node. `level` is `company | department | team` (PRF-17 re-added `team`, between
 * `department` and an individual `Goal` — there is no first-class Team model, so a team-level
 * Objective's scope is its own `ownerPersonId`'s reporting line, exactly like every other "team"
 * scope in this codebase). `parentObjectiveId` is a real FK: a child objective is meaningless
 * without its parent.
 */
export const objectiveSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  level: goalLevelSchema,
  parentObjectiveId: uuidSchema.nullable(),
  departmentId: uuidSchema.nullable(),
  departmentName: z.string().nullable(),
  kraCategory: z.string().nullable(),
  ownerPersonId: uuidSchema,
  ownerName: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  startDate: dateOnlySchema,
  dueDate: dateOnlySchema,
  status: objectiveStatusSchema,
  keyResults: z.array(keyResultSchema),
  createdAt: instantSchema,
});
export type Objective = z.infer<typeof objectiveSchema>;

/**
 * The rolled-up card `getGoalsOverview` returns — one per cascade node, carrying its own
 * on-read-computed rollup progress (weighted mean of its children's) rather than a stored value,
 * per the plan's "Objective rollup" gap-analysis item. Deliberately lighter than `objectiveSchema`:
 * no embedded `keyResults` array, since the overview's whole point is one cheap round trip for a
 * list of cards, not full detail for each.
 */
export const objectiveSummarySchema = z.object({
  id: uuidSchema,
  level: goalLevelSchema,
  parentObjectiveId: uuidSchema.nullable(),
  departmentId: uuidSchema.nullable(),
  departmentName: z.string().nullable(),
  /** Only meaningful for `level: 'team'` — whose reporting line the team is (PRF-17). Always
   *  present (every Objective has an owner) but only rendered for the team card. */
  ownerPersonId: uuidSchema,
  ownerName: z.string(),
  kraCategory: z.string().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  dueDate: dateOnlySchema,
  status: objectiveStatusSchema,
  /** Weighted mean of children's progress, computed on read — see the plan's rollup note. */
  rollupProgressPercent: percentSchema,
  confidence: goalConfidenceSchema.nullable(),
  /** Up to 2 of the Objective's own `KeyResult` rows (T20) — see `goalKeyResultSummarySchema`'s
   * header for how `currentValue`/`progressPercent` are derived on read. Ordered by `weightPercent`
   * descending so the card shows its most-weighted key results first. */
  keyResults: z.array(goalKeyResultSummarySchema),
});
export type ObjectiveSummary = z.infer<typeof objectiveSummarySchema>;

/** Per-person key-result tracking, one row per `KeyResult` a `Goal` is attached to. */
export const goalKeyResultSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  goalId: uuidSchema,
  /** Null for a purely individual key result not rolled up to any parent Objective KeyResult —
   * see GoalKeyResult.keyResultId in schema.prisma. */
  keyResultId: uuidSchema.nullable(),
  title: z.string(),
  measureType: goalMeasureTypeSchema,
  currency: currencySchema.nullable(),
  startValue: measureValueSchema,
  /** Daily Work Planning Phase 6 (boundary, not a merge) — when `employeeTargetId` is set, this is
   * DERIVED on read from that `EmployeeTarget`'s own computed achievement instead of the stored
   * manual value; see goal.ts. Default (no `employeeTargetId`) stays exactly the manual value it
   * always was. */
  currentValue: measureValueSchema,
  targetValue: measureValueSchema,
  progressPercent: percentSchema,
  /** Null (default) = fully manual, unchanged from before this field existed. Set = `currentValue`/
   * `progressPercent` above are derived from the linked `EmployeeTarget`, and a manual
   * `keyResultUpdates` write against this key result in `createCheckIn` is rejected (422). */
  employeeTargetId: uuidSchema.nullable(),
});
export type GoalKeyResult = z.infer<typeof goalKeyResultSchema>;

/**
 * One person's goal. `personId` is ALWAYS a `Person` id, never an `Employment` id — see
 * `apps/api/src/lib/person-scope.ts`'s header comment for the bug class this avoids.
 * `workflowInstanceId` is deliberately not an FK, same reasoning as `LeaveRequest`'s (one engine
 * serves many entity types; history must outlive a purged instance) — and is nullable because
 * workflow attachment is conditional on the org having opted in (see this file's header).
 */
export const goalSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  personName: z.string(),
  objectiveId: uuidSchema.nullable(),
  departmentId: uuidSchema.nullable(),
  kraCategory: z.string().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  startDate: dateOnlySchema,
  dueDate: dateOnlySchema,
  weightPercent: percentSchema,
  progressPercent: percentSchema,
  status: goalStatusSchema,
  workflowInstanceId: uuidSchema.nullable(),
  /** Set when this goal was created "from" a `GoalTemplate` — reporting only, same
   * deliberately-not-a-foreign-key posture as `workflowInstanceId` above. Content is snapshotted at
   * creation; this id is never used to re-derive a live value — see `goal-template.contract.ts`'s
   * header and `Goal.sourceTemplateId`'s comment in schema.prisma. */
  sourceTemplateId: uuidSchema.nullable(),
  keyResults: z.array(goalKeyResultSchema),
  createdAt: instantSchema,
});
export type Goal = z.infer<typeof goalSchema>;

/**
 * The lightweight row `getGoalsOverview`'s `individualGoals` array returns. Carries up to 2 of the
 * goal's own `GoalKeyResult` rows (T20's gap-analysis item — see `goalKeyResultSummarySchema`'s
 * header) so a card can show a compact target/current row without a second round trip per card;
 * still deliberately lighter than the full `goalSchema.keyResults` (every `GoalKeyResult`, no cap).
 */
export const goalSummarySchema = z.object({
  id: uuidSchema,
  personId: uuidSchema,
  personName: z.string(),
  objectiveId: uuidSchema.nullable(),
  kraCategory: z.string().nullable(),
  title: z.string(),
  dueDate: dateOnlySchema,
  weightPercent: percentSchema,
  progressPercent: percentSchema,
  status: goalStatusSchema,
  keyResults: z.array(goalKeyResultSummarySchema),
});
export type GoalSummary = z.infer<typeof goalSummarySchema>;

/**
 * Append-only — the whole reason `Goal` needs no effective-dating (plan's Data model section).
 * `previousProgressPercent` lets a client show a delta without a window function. `source:
 * 'ai_assisted'` is what makes an AI-drafted narrative auditable and what an `ApprovalCard`-style
 * surface keys `proposedByAi` off of.
 */
export const goalCheckInSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  goalId: uuidSchema,
  authorPersonId: uuidSchema,
  authorName: z.string(),
  progressPercent: percentSchema,
  previousProgressPercent: percentSchema,
  confidence: goalConfidenceSchema,
  note: z.string().nullable(),
  source: goalCheckInSourceSchema,
  createdAt: instantSchema,
});
export type GoalCheckIn = z.infer<typeof goalCheckInSchema>;

export const goalCommentSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  goalId: uuidSchema,
  authorPersonId: uuidSchema,
  authorName: z.string(),
  body: z.string(),
  createdAt: instantSchema,
});
export type GoalComment = z.infer<typeof goalCommentSchema>;

export const goalsOverviewSchema = z.object({
  companyObjectives: z.array(objectiveSummarySchema),
  departmentObjectives: z.array(objectiveSummarySchema),
  /** PRF-17: every active `level: 'team'` Objective whose owner is the viewed person's current
   *  manager (their own team's objective) or the viewed person themselves (viewing as that
   *  team's manager) — see the route for the exact resolution. */
  teamObjectives: z.array(objectiveSummarySchema),
  individualGoals: z.array(goalSummarySchema),
});
export type GoalsOverview = z.infer<typeof goalsOverviewSchema>;

// ---------------------------------------------------------------------------
// Requests — goals
// ---------------------------------------------------------------------------

export const listGoalsQuerySchema = paginationQuerySchema.extend({
  /** Defaults to the caller's own goals. */
  personId: uuidSchema.optional(),
  objectiveId: uuidSchema.optional(),
  status: goalStatusSchema.optional(),
});

const goalKeyResultInputSchema = z.object({
  keyResultId: uuidSchema.optional(),
  title: z.string().trim().min(1).max(200),
  measureType: goalMeasureTypeSchema,
  currency: currencySchema.optional(),
  startValue: measureValueSchema,
  targetValue: measureValueSchema,
  /** Daily Work Planning Phase 6 (opt-in, boundary not a merge) — must belong to the SAME person as
   * this goal (the route 404s otherwise, same "unknown reference" convention project.ts uses for
   * `milestoneId`). This is the only point a `GoalKeyResult` can be LINKED to an `EmployeeTarget` —
   * there is no general "edit key result" route today, so goal creation is the natural, minimal
   * surface rather than a new write endpoint. `unlinkKeyResultTarget` below is the one-way-back-out
   * counterpart (clears it to null, reverting to manual entry) — still not a general editor. */
  employeeTargetId: uuidSchema.optional(),
});

/**
 * `dueDate >= startDate`, refined below — matches `createLeaveRequestBodySchema`'s shape exactly.
 * `personId` is optional: a caller creating for themself omits it (defaults to `actor.personId`); a
 * manager assigning to a report supplies it, and the route's `goal:create` check decides whether
 * that's allowed (self by default, or a manager over a resolved report — see the route).
 */
export const createGoalBodySchema = z
  .object({
    personId: uuidSchema.optional(),
    objectiveId: uuidSchema.optional(),
    /** Start this goal from a `GoalTemplate` (goal-template.contract.ts) — its current content
     * (kraCategory/description when the caller omits them, key results when `keyResults` below is
     * omitted) is SNAPSHOTTED onto this goal's row at creation, never a live reference. The route
     * 422s the same way the manual over-100%-weight path does if applying it would push the
     * person's active-goal weight total over 100 — see goal.ts's createGoal. */
    templateId: uuidSchema.optional(),
    kraCategory: z.string().trim().max(100).optional(),
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().max(2000).optional(),
    startDate: dateOnlySchema,
    dueDate: dateOnlySchema,
    weightPercent: percentSchema,
    keyResults: z.array(goalKeyResultInputSchema).max(20).optional(),
    /**
     * `false` saves the goal as a `draft` (no approval started) for the owner to finish and send
     * later with `submitGoal`. Omitted or `true` submits it straight away — `pending_approval` when
     * a `goal` workflow is configured, else `active` — which is what every client did before the
     * explicit submit route existed (performance remediation R5: installed mobile builds send no
     * flag and must keep getting that behaviour).
     */
    submit: z.boolean().optional(),
  })
  .refine((body) => body.dueDate >= body.startDate, {
    message: 'dueDate must be on or after startDate',
    path: ['dueDate'],
  });

/**
 * Every field optional — a partial update. The route decides whether a change to
 * `weightPercent`/`targetValue` (via `keyResults`)/`dueDate` on an already-approved goal re-enters
 * `pending_approval` (the plan's "Mid-cycle goal revision" item); title/description-only edits do
 * not. `dueDate`/`startDate` are both optional here (unlike create) since an update may touch only
 * one of the pair — the route re-validates the combined pair against whichever value is unchanged.
 */
export const updateGoalBodySchema = z.object({
  objectiveId: uuidSchema.nullable().optional(),
  title: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(2000).optional(),
  kraCategory: z.string().trim().max(100).optional(),
  startDate: dateOnlySchema.optional(),
  dueDate: dateOnlySchema.optional(),
  weightPercent: percentSchema.optional(),
  /**
   * @deprecated Use the explicit transition routes (`submitGoal`, `closeGoal`, `archiveGoal`);
   * the server rejects illegal transitions (409). Still accepted so installed mobile builds keep
   * working (performance remediation R5); removed after the next mobile release.
   */
  status: goalStatusSchema
    .optional()
    .describe(
      'deprecated: use the explicit transition routes; the server rejects illegal transitions',
    ),
});

/** Submit a draft goal: enters `pending_approval` when a `goal` workflow is configured for the
 * org, else becomes `active` directly. No body — the goal as saved is what is submitted. */
export const submitGoalBodySchema = z.object({});

/** Close an `active`/`at_risk` goal with its outcome — the only way to reach `achieved`/`missed`. */
export const closeGoalBodySchema = z.object({
  outcome: z.enum(['achieved', 'missed']),
  comment: z.string().trim().max(2000).optional(),
});
export type CloseGoalBody = z.infer<typeof closeGoalBodySchema>;

export const decideGoalBodySchema = z.object({
  comment: z.string().max(500).optional(),
});

export const listCheckInsQuerySchema = paginationQuerySchema;

export const createCheckInBodySchema = z.object({
  progressPercent: percentSchema,
  confidence: goalConfidenceSchema,
  note: z.string().trim().max(2000).optional(),
  /** Defaults to 'self' at the route; only the AI-draft save path sends 'ai_assisted' explicitly. */
  source: goalCheckInSourceSchema.optional(),
  keyResultUpdates: z
    .array(z.object({ goalKeyResultId: uuidSchema, currentValue: measureValueSchema }))
    .max(20)
    .optional(),
});

export const listCommentsQuerySchema = paginationQuerySchema;

export const createCommentBodySchema = z.object({
  body: z.string().trim().min(1).max(2000),
});

/** Input to the AI drafting tool — see the plan's PII-boundary note: no personId/email/comp/DOB. */
export const draftCheckInBodySchema = z.object({
  tone: z.enum(['concise', 'formal', 'encouraging']).optional(),
  maxWords: z.number().int().min(20).max(400).optional(),
});

export const draftCheckInResponseSchema = z.object({
  draftNote: z.string(),
  suggestedConfidence: goalConfidenceSchema,
});

// ---------------------------------------------------------------------------
// Requests — objectives
// ---------------------------------------------------------------------------

export const listObjectivesQuerySchema = paginationQuerySchema.extend({
  level: goalLevelSchema.optional(),
  departmentId: uuidSchema.optional(),
  status: objectiveStatusSchema.optional(),
});

const keyResultInputSchema = z.object({
  title: z.string().trim().min(1).max(200),
  measureType: goalMeasureTypeSchema,
  targetValue: measureValueSchema,
  currency: currencySchema.optional(),
  weightPercent: percentSchema,
});

/**
 * `departmentId` required whenever `level !== 'company'` (a team Objective still belongs to one
 * department, PRF-17) — refined below. Company/department-level creation is scope-gated (not a new
 * permission): `goal:manage` granted at `org` scope lets hr_admin/org_owner create company
 * Objectives; granted at `department` scope lets hr_manager create department AND team ones in that
 * department. The route enforces the scope check; this schema only enforces shape.
 *
 * `ownerPersonId`, team-level only: whose team this is — omitted defaults to the caller (a manager
 * creating their own team's objective); HR creating it on a manager's behalf supplies it
 * explicitly. Ignored for company/department, which always own to the creator.
 */
export const createObjectiveBodySchema = z
  .object({
    level: goalLevelSchema,
    parentObjectiveId: uuidSchema.optional(),
    departmentId: uuidSchema.optional(),
    ownerPersonId: uuidSchema.optional(),
    kraCategory: z.string().trim().max(100).optional(),
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().max(2000).optional(),
    startDate: dateOnlySchema,
    dueDate: dateOnlySchema,
    keyResults: z.array(keyResultInputSchema).max(20).optional(),
  })
  .refine((body) => body.dueDate >= body.startDate, {
    message: 'dueDate must be on or after startDate',
    path: ['dueDate'],
  })
  .refine((body) => body.level === 'company' || !!body.departmentId, {
    message: 'departmentId is required unless level is "company"',
    path: ['departmentId'],
  });

export const updateObjectiveBodySchema = z.object({
  kraCategory: z.string().trim().max(100).optional(),
  title: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(2000).optional(),
  startDate: dateOnlySchema.optional(),
  dueDate: dateOnlySchema.optional(),
  status: objectiveStatusSchema.optional(),
});

export const listObjectiveGoalsQuerySchema = paginationQuerySchema;

export const getGoalsOverviewQuerySchema = z.object({
  /** Defaults to the caller. A manager may pass a report's id; see the route's per-array scoping. */
  personId: uuidSchema.optional(),
  /** Manager opt-in: when true, `individualGoals` additionally includes reports' goals. */
  team: z.coerce.boolean().optional(),
  /**
   * Oversight mode — browse one department's Objectives + every goal snapshotted to it, instead of
   * one person's own view. Takes precedence over `personId`/`team` when set (they express "whose
   * personal overview", this expresses "which department's roster"). Gated on the same rule the
   * route already applies to department Objective visibility: the org-wide `goal:manage` grant, or
   * a `goal:manage` grant scoped to this specific department — see the route's header. Individual
   * employees browsing their own overview never need this; it exists for the Performance Insights
   * "Goal Analytics" drill-in.
   */
  departmentId: uuidSchema.optional(),
});

// ---------------------------------------------------------------------------
// Contract — /goals
// ---------------------------------------------------------------------------

export const goalContract = c.router(
  {
    listGoals: {
      method: 'GET',
      path: '/',
      query: listGoalsQuerySchema,
      responses: {
        200: paginatedSchema(goalSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "List goals in the caller's visible scope",
      metadata: routeMeta('goal:read'),
    },

    getGoal: {
      method: 'GET',
      path: '/:goalId',
      responses: {
        200: goalSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get one goal',
      metadata: routeMeta('goal:read'),
    },

    createGoal: {
      method: 'POST',
      path: '/',
      body: createGoalBodySchema,
      responses: {
        201: goalSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Create a goal for the caller, or a manager assigning to a report — starts the conditional approval workflow when one is configured, or saves a draft with submit:false',
      metadata: routeMeta(null),
    },

    updateGoal: {
      method: 'PATCH',
      path: '/:goalId',
      body: updateGoalBodySchema,
      responses: {
        200: goalSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Update a goal — weightPercent/dueDate changes on an approved goal re-enter pending_approval; 409 on an achieved/missed/cancelled goal or an illegal (deprecated) status write',
      metadata: routeMeta('goal:manage'),
    },

    archiveGoal: {
      method: 'POST',
      path: '/:goalId/archive',
      body: z.object({}),
      responses: {
        200: goalSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        "Archive (cancel) a goal — supersedes a pending approval so it leaves the approver's inbox; 409 once achieved/missed/cancelled",
      metadata: routeMeta('goal:manage'),
    },

    approveGoal: {
      method: 'POST',
      path: '/:goalId/approve',
      body: decideGoalBodySchema,
      responses: {
        200: goalSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Approve the step currently awaiting the caller',
      metadata: routeMeta(null),
    },

    rejectGoal: {
      method: 'POST',
      path: '/:goalId/reject',
      body: decideGoalBodySchema,
      responses: {
        200: goalSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Reject the step currently awaiting the caller',
      metadata: routeMeta(null),
    },

    listCheckIns: {
      method: 'GET',
      path: '/:goalId/check-ins',
      query: listCheckInsQuerySchema,
      responses: {
        200: paginatedSchema(goalCheckInSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "List a goal's check-in history, newest first",
      metadata: routeMeta('goal:read'),
    },

    createCheckIn: {
      method: 'POST',
      path: '/:goalId/check-ins',
      body: createCheckInBodySchema,
      responses: {
        201: goalCheckInSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        "Record a check-in on an active/at_risk goal (409 otherwise) — recomputes the goal's and its key results' progressPercent under the same transaction",
      metadata: routeMeta('goal.checkin:create'),
    },

    /**
     * Daily Work Planning Phase 6 follow-up — the unlink counterpart to `createGoal`'s
     * `keyResults[].employeeTargetId` (the only point a `GoalKeyResult` can be LINKED; see
     * `goalKeyResultInputSchema`'s header). Gated on `goal:create`, not `goal:manage` — deliberately
     * the SAME permission that gates creating the link in the first place (self scope for an
     * employee's own goal, `reporting_tree`/wider for a manager/HR role over a report's), not the
     * `goal:manage`-gated pattern `updateGoal`/`archiveGoal` use for other goal-level edits. See
     * goal.ts's `unlinkKeyResultTarget` for the full reasoning, including why a clean 403 (visible
     * but not writable) never actually occurs for this particular gate — `goal:create` and
     * `goal:read` are always granted together at the same scope in this permission matrix, so an
     * out-of-scope caller always 404s here, same as every other resolved-by-id miss in this file.
     * 412 when the key result is already unlinked — mirrors `approveGoal`/`rejectGoal`'s 412 for
     * "nothing here to decide".
     */
    unlinkKeyResultTarget: {
      method: 'POST',
      path: '/:goalId/key-results/:keyResultId/unlink-target',
      pathParams: z.object({ goalId: uuidSchema, keyResultId: uuidSchema }),
      body: z.object({}),
      responses: {
        200: goalKeyResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        412: errorBodySchema,
      },
      summary:
        'Unlink a key result from its EmployeeTarget, reverting it to manual entry — 412 if it was not linked',
      metadata: routeMeta('goal:create'),
    },

    listComments: {
      method: 'GET',
      path: '/:goalId/comments',
      query: listCommentsQuerySchema,
      responses: {
        200: paginatedSchema(goalCommentSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "List a goal's comments",
      metadata: routeMeta('goal:read'),
    },

    createComment: {
      method: 'POST',
      path: '/:goalId/comments',
      body: createCommentBodySchema,
      responses: {
        201: goalCommentSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Add a comment to a goal',
      metadata: routeMeta('goal:read'),
    },

    draftCheckIn: {
      method: 'POST',
      path: '/:goalId/draft-check-in',
      body: draftCheckInBodySchema,
      responses: {
        200: draftCheckInResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
        503: errorBodySchema,
      },
      summary:
        'AI-drafted check-in narrative suggestion — read-only, persists nothing; 422 when the built ' +
        'prompt trips a blocking DLP rule, 503 when no provider is configured',
      metadata: routeMeta(['ai.copilot:use', 'goal.checkin:create', 'goal:read']),
    },

    // --- Explicit goal transitions (performance remediation §6) — replace client `status` writes. ---

    submitGoal: {
      method: 'POST',
      path: '/:goalId/submit',
      body: submitGoalBodySchema,
      responses: {
        200: goalSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Submit a draft goal — by its owner or a manager holding goal:manage over them; pending_approval when a goal workflow is configured, else active; 409 unless draft. Requires Idempotency-Key',
      // Not `goal:manage`: the OWNER submits their own goal, and an employee holds only goal:read /
      // goal:create at self scope. The handler requires goal:read over the goal (404 otherwise),
      // then owner-or-goal:manage (403).
      metadata: routeMeta('goal:read'),
    },

    closeGoal: {
      method: 'POST',
      path: '/:goalId/close',
      body: closeGoalBodySchema,
      responses: {
        200: goalSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Close an active/at_risk goal as achieved or missed — by its owner or a manager holding goal:manage over them; 409 from any other status. Requires Idempotency-Key',
      // Same shape as submitGoal: owner or manager, so the gate is goal:read and the handler decides.
      // No approval step for closure (plan §5A step 5): the owner proposes the outcome and either
      // party records it; the manager's override is an ordinary goal:manage close.
      metadata: routeMeta('goal:read'),
    },
  },
  { pathPrefix: '/goals' },
);

// ---------------------------------------------------------------------------
// Contract — /objectives
// ---------------------------------------------------------------------------

export const objectiveContract = c.router(
  {
    listObjectives: {
      method: 'GET',
      path: '/',
      query: listObjectivesQuerySchema,
      responses: {
        200: paginatedSchema(objectiveSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "List Objectives in the caller's visible scope",
      metadata: routeMeta(null),
    },

    // NOTE: declared before `getObjective` below, deliberately. Both this route's path (`/overview`)
    // and `getObjective`'s (`/:objectiveId`) could match "GET /objectives/overview" — Express (and
    // therefore `tsRestRouter`, which mounts routes in this object's key order) tries routes in
    // registration order and uses the first match, so the literal path must be registered before the
    // wildcard one or "overview" gets swallowed as an `:objectiveId` value and fails at the database
    // with an invalid-UUID error.
    getGoalsOverview: {
      method: 'GET',
      path: '/overview',
      query: getGoalsOverviewQuerySchema,
      responses: {
        200: goalsOverviewSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'The three-tier composed view (company Objectives, department Objectives, individual goals) in one round trip',
      metadata: routeMeta(null),
    },

    getObjective: {
      method: 'GET',
      path: '/:objectiveId',
      responses: {
        200: objectiveSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get one Objective',
      metadata: routeMeta(null),
    },

    createObjective: {
      method: 'POST',
      path: '/',
      body: createObjectiveBodySchema,
      responses: {
        201: objectiveSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Create a company or department Objective — scope-gated via goal:manage, not a separate permission',
      metadata: routeMeta('goal:manage'),
    },

    updateObjective: {
      method: 'PATCH',
      path: '/:objectiveId',
      body: updateObjectiveBodySchema,
      responses: {
        200: objectiveSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Update an Objective',
      metadata: routeMeta('goal:manage'),
    },

    archiveObjective: {
      method: 'POST',
      path: '/:objectiveId/archive',
      body: z.object({}),
      responses: {
        200: objectiveSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Archive an Objective',
      metadata: routeMeta('goal:manage'),
    },

    listObjectiveGoals: {
      method: 'GET',
      path: '/:objectiveId/goals',
      query: listObjectiveGoalsQuerySchema,
      responses: {
        200: paginatedSchema(goalSummarySchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'The individual goals rolled up under this Objective — HR/manager oversight surface',
      metadata: routeMeta('goal:manage'),
    },
  },
  { pathPrefix: '/objectives' },
);
